export type PackagingOption = {
  id: string;
  label: string;
  sizeLbs: number;
  isDefault?: boolean;
};

export type PackagingSelection = {
  optionId: string;
  count: number;
};

const KG_TO_LB = 2.20462;

export function lbsToUnit(qtyLbs: number, unit: 'LB' | 'KG') {
  if (unit === 'KG') return Math.round((qtyLbs / KG_TO_LB) * 100) / 100;
  return Math.round(qtyLbs * 10) / 10;
}

export function unitToLbs(qty: number, unit: 'LB' | 'KG') {
  if (unit === 'KG') return qty * KG_TO_LB;
  return qty;
}

export function selectionsTotalLbs(
  selections: PackagingSelection[],
  options: PackagingOption[],
): number {
  const byId = new Map(options.map((o) => [o.id, o.sizeLbs]));
  return selections.reduce((sum, row) => {
    const size = byId.get(row.optionId);
    if (!size || row.count <= 0) return sum;
    return sum + size * row.count;
  }, 0);
}

export function isQuantityAchievableWithPackaging(
  targetLbs: number,
  options: PackagingOption[],
): boolean {
  if (!Number.isFinite(targetLbs) || targetLbs <= 0) return false;
  const sizes = options.map((o) => o.sizeLbs).filter((s) => s > 0);
  if (sizes.length === 0) return true;
  if (sizes.length === 1) {
    const ratio = targetLbs / sizes[0];
    return Math.abs(ratio - Math.round(ratio)) < 1e-4 && ratio >= 1 - 1e-4;
  }
  const scale = 1000;
  const target = Math.round(targetLbs * scale);
  const coins = sizes.map((s) => Math.round(s * scale));
  const dp = new Array(target + 1).fill(false);
  dp[0] = true;
  for (const coin of coins) {
    for (let i = coin; i <= target; i += 1) {
      if (dp[i - coin]) dp[i] = true;
    }
  }
  return dp[target];
}

export function formatPackagingSummary(
  selections: PackagingSelection[],
  options: PackagingOption[],
): string {
  const byId = new Map(options.map((o) => [o.id, o]));
  return selections
    .filter((s) => s.count > 0)
    .map((s) => {
      const opt = byId.get(s.optionId);
      if (!opt) return null;
      return `${s.count}× ${opt.label}`;
    })
    .filter(Boolean)
    .join(' + ');
}

export function defaultSelections(
  options: PackagingOption[],
  packageCount = 1,
): PackagingSelection[] {
  const def = options.find((o) => o) || options[0];
  if (!def) return [];
  const preferred =
    options.find((o) => (o as { isDefault?: boolean }).isDefault) || def;
  return [{ optionId: preferred.id, count: Math.max(1, packageCount) }];
}
